/**
 * Toolbar — the AI Actions page toolbar: view switcher dropdown
 * (Open / Done / Dismissed / Archived) and mark-all-as-read. Purely
 * presentational; all state and handlers come from ProposalListPanel.
 *
 * The Priority/Date sort toggle was removed 2026-09-01: the list always
 * groups by priority band and sorts newest-first within each band (see
 * utils/groupProposalsByTier).
 */
import { CheckDone01, ChevronDown } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { ToolbarSection } from '@/lib/base/page-layout';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import type { AiActionsView } from '@/lib/localStorage/aiActionsSortPrefs';

import { QuickDraftMenu } from '../QuickDraftMenu';
import { VIEW_TABS } from './types';

interface ToolbarProps {
  view: AiActionsView;
  orgId: number | undefined;
  unreadCount: number;
  onMarkAllAsRead: () => void;
  markAllAsReadPending: boolean;
}

export function Toolbar({
  view,
  orgId,
  unreadCount,
  onMarkAllAsRead,
  markAllAsReadPending,
}: ToolbarProps) {
  const navigate = useNavigate();

  // View-switcher dropdown wiring: the view lives in the URL, so selecting
  // one navigates to its route (menu-level onAction keeps item JSX free of
  // inline callbacks; selectedKeys marks the current view with a check).
  const handleViewMenuAction = useCallback(
    (key: Key) => {
      if (orgId == null) {
        return;
      }
      const next = String(key) as AiActionsView;
      navigate(
        next === 'open' ? `/${orgId}/actions` : `/${orgId}/actions/${next}`
      );
    },
    [orgId, navigate]
  );
  const selectedViewKeys = useMemo(() => [view], [view]);
  const activeViewTab = VIEW_TABS.find(tab => tab.id === view) ?? VIEW_TABS[0];
  const activeViewLabel = activeViewTab.label;
  const ActiveViewIcon = activeViewTab.icon;

  return (
    <>
      <ToolbarSection>
        {/* View switcher — a labeled dropdown selecting what the list
              renders (Open / Done / Dismissed / Archived). */}
        <Dropdown.Root instance='ai-actions-view'>
          <Button
            color='tertiary'
            size='sm'
            aria-label={`View: ${activeViewLabel}`}
            className='!h-auto shrink-0 !rounded-md !border !border-[var(--color-border-secondary)] !bg-[var(--color-bg-primary)] !px-[10px] !py-[4px] !text-[12px] !font-medium !leading-[18px] before:!hidden'
            iconLeading={
              <ActiveViewIcon
                aria-hidden='true'
                className='!size-3.5 !text-[var(--color-text-tertiary)]'
              />
            }
            iconTrailing={
              <ChevronDown
                aria-hidden='true'
                className='!size-4 !text-[var(--color-text-tertiary)]'
                strokeWidth={2.25}
              />
            }
          >
            {activeViewLabel}
          </Button>
          <Dropdown.Popover placement='bottom left' className='min-w-[168px]'>
            <Dropdown.Menu
              selectedKeys={selectedViewKeys}
              onAction={handleViewMenuAction}
            >
              {VIEW_TABS.map(tab => (
                <Dropdown.Item
                  key={tab.id}
                  id={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      </ToolbarSection>
      <div className='ml-auto flex items-center gap-2'>
        {view === 'open' && orgId != null && <QuickDraftMenu orgId={orgId} />}
        {view === 'open' && unreadCount > 0 && (
          <Tooltip title='Mark all as read' placement='bottom'>
            <TooltipTrigger
              aria-label='Mark all as read'
              onPress={onMarkAllAsRead}
              isDisabled={markAllAsReadPending}
              className='flex size-[26px] shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] transition-colors cursor-pointer hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <CheckDone01 aria-hidden='true' className='size-4' />
            </TooltipTrigger>
          </Tooltip>
        )}
      </div>
    </>
  );
}
